"use strict";

const _ = require("lodash");
const PopUp = require("@gluenetworks/steve_ui_widgets").PopUp;
const MultipleSelectListControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection
    .Multiple.FilterableSelectList;

class ColumnSettingsRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-column-settings-gl");
        this._rootElem.component = this._model;
        this._rootElem.setAttribute("title", "Columns");

        this._settingsIcon = document.createElement("actionable-icon-gl");
        this._settingsIcon.dataset.icon = "common.column_settings";
        this._settingsIcon.setAttribute("data-dark-theme", "true");

        this._allColumns = [];
        // for example, [{
        //     "label": "Column1",
        //     "value": "value1"
        // }, {
        //     "label": "Column2",
        //     "value": "value2"
        // }];
        this._selectedColumns = [
            // "value2"
        ];
        this._filteredColumns = [];

        this._settingsList = new MultipleSelectListControl({
            disabled: false,
            invalid: false,
            visible: true,
            darkTheme: true, // currently disabled, based on feedback
            theme: "",
            comboBoxMode: true,
            value: this._selectedColumns,
            options: this._filteredColumns,
            // Filter options
            showFilter: true,
            placeholder: "Search columns...",
            sortAlphabetically: true,
        });

        this._settingsPopupContent = document.createElement("div");
        this._settingsPopupContent.classList.add("column-settings-content");

        this._settingsPopup = new PopUp({
            hideDelayTime: 1000,
            anchor: this._settingsIcon,
            anchorPoint: "bottomLeft",
            displayDirection: "bottomRight",
            content: this._settingsPopupContent,
            parent: this._settingsIcon,
            allowClickInside: true,
        });

        this._arrowUp = document.createElement("div");
        this._arrowUp.classList.add("arrow");

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._settingsList.on(
            "Value",
            (this._onSettingsListValueChange = this._onSettingsListValueChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._settingsList.removeListener("Value", this._onSettingsListValueChange);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [this._settingsList.shutdown(), this._settingsPopup.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._settingsIcon.remove();
                this._settingsIcon = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._logger = undefined;
                this._allColumns = undefined;
                this._filteredColumns = undefined;
                this._selectedColumns = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ColumnSettingsRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering.
     */
    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._settingsIcon);
            }
            if (!this._settingsPopupContent.hasChildNodes()) {
                this._settingsPopupContent.appendChild(this._arrowUp);
                this._settingsPopupContent.appendChild(this._settingsList.renderer.rootElement);
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _updateColumnSettings(columns) {
        if (columns && columns instanceof Array && columns.length) {
            this._allColumns = columns.filter(
                (column) => !column.hasOwnProperty("editable") || column.editable
            );
            this._settingsList.setOptions(this._allColumns);
            this._selectedColumns = this._allColumns
                .filter((column) => column.shown)
                .map((column) => column.value);
            this._settingsList.setValue(this._selectedColumns);
        }
    }

    _onSettingsListValueChange(selectedColumns) {
        // Find newly selected columns (not in previous selection)
        const previouslySelected = this._selectedColumns || [];
        const newlySelected = selectedColumns.filter(
            (value) => previouslySelected.indexOf(value) === -1
        );

        // Create a set for quick lookup of column shown state
        const selectedSet = new Set(selectedColumns);

        // Update shown state for all columns
        let newColumnSettings = this._allColumns.map((column) => {
            let newColumn = _.cloneDeep(column);
            newColumn.shown = selectedSet.has(column.value);
            return newColumn;
        });

        // If there are newly selected columns, move them to the end
        if (newlySelected.length > 0) {
            const shownColumns = [];
            const hiddenColumns = [];
            const newlySelectedColumns = [];

            newColumnSettings.forEach((column) => {
                if (newlySelected.indexOf(column.value) !== -1) {
                    newlySelectedColumns.push(column);
                } else if (column.shown) {
                    shownColumns.push(column);
                } else {
                    hiddenColumns.push(column);
                }
            });

            // Reorder: previously shown + newly selected + hidden
            newColumnSettings = [...shownColumns, ...newlySelectedColumns, ...hiddenColumns];
        }

        let haveSomethingChanged = this._allColumns.some((column, i) => {
            return (
                column.shown !== newColumnSettings[i].shown ||
                column.value !== newColumnSettings[i].value
            );
        });

        if (haveSomethingChanged) {
            this._model.columnSettingsUserChange(newColumnSettings);
            this._allColumns = newColumnSettings;
            this._selectedColumns = selectedColumns;
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = ColumnSettingsRenderer;
